"use client";

import clsx from "clsx";
import { useState, type ReactNode } from "react";
import { Modal } from "./Dialog";
import { Icon } from "./icons";
import { CloseButton } from "./PhonePopup";

/**
 * An ⓘ button that opens its content in a window in the middle of the screen (on every device; closed by its ✕, a
 * press beside it or Escape) — a setting's description on phones, the vote's rules, a table's data source. `title`:
 * the window's heading (else the label); `compact`: the smaller icon of a row.
 */
export function InfoPopover({ label, title = label, compact = false, children }: { label: string; title?: string; compact?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label}
        className={clsx("inline-flex cursor-pointer items-center justify-center transition-colors", compact ? "h-5 w-5 rounded" : "h-6 w-6 rounded-md", open ? "bg-accent-soft text-accent-ink" : "text-muted hover:bg-surface-2 hover:text-ink")}
      >
        <Icon name="info" className="h-3.5 w-3.5" />
      </button>
      {open && (
        <Modal label={label} onClose={() => setOpen(false)} size="wide">
          {/* the window is in a portal: its presses do not reach what holds the ⓘ (a row, a header) */}
          <div onClick={(e) => e.stopPropagation()} className="text-left text-[13px] font-normal tracking-normal normal-case text-ink">
            <div className="mb-2 flex items-start gap-2">
              <h2 className="min-w-0 flex-1 text-[15px] font-semibold">{title}</h2>
              <CloseButton onClose={() => setOpen(false)} className="-mt-1 -mr-1" />
            </div>
            {children}
          </div>
        </Modal>
      )}
    </>
  );
}
